/**
 * Which run a run-scoped page opens on when its URL names none — owner ruling DEC-99
 * (2026-10-01), `docs/adr/9039`, generalising ADR 6011 (the Pricing pages, 2026-09-30) to every
 * run-scoped screen; amended by the owner on 2026-10-10: the newest always wins on arrival.
 *
 * **The precedence, in one place** ({@link resolveRun}, pure):
 *
 *   1. a run the URL names — always wins, nothing is overridden;
 *   2. the newest eligible run of the page's kind — the first run the page's own list offers,
 *      which is the chapter's statement of "eligible" (ADR 6011's rule for price runs);
 *   3. none — «Scegli un run» (ADR 9033) is now the state of a page with no run to open.
 *
 * **The URL is the only memory.** A run the planner picked by hand lives in the address bar, and
 * the shell carries the query string while the planner moves within the session; nothing is
 * remembered across visits any more. (Until 2026-10-10 a per-browser `localStorage` choice outranked
 * the newest; a value left behind by that version is forgotten the next time the planner picks,
 * and is never read.)
 *
 * Step 2 is written INTO the URL with `history.replaceState` by the caller's `write`, so every
 * read is still bounded by a run the address bar names (CLAUDE.md §3.4, G16) and Back leaves the
 * page rather than returning to the no-run address.
 *
 * **What the page says.** A run chosen for the planner is never silently in force: while — and
 * only while — the URL still names the run this module chose, {@link RunDefaultNote} says it was
 * the most recent. «Il più recente» ({@link NEWEST_RUN_OPTION}) in a picker drops the run from the
 * URL, so the page follows the newest again.
 */

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthSession } from '../auth/session';

/**
 * The `localStorage` prefix of the choice a planner's browser kept until 2026-10-10. Nothing reads
 * it now; it is named so the leftover can be forgotten. Versioned so a later shape can ignore it.
 */
export const RUN_MEMORY_PREFIX = 'bata.run.chosen.v1';

/** The market part of a key when the page's run names its own market (no `?country=` on the page). */
export const ANY_MARKET = '*';

/** The value of the pickers' «Il più recente» option — not a run id (run ids are never `@…`). */
export const NEWEST_RUN_OPTION = '@newest';

/** What a page's run choice is scoped by, besides the planner. */
export interface RunScope {
  /** The kind of run the page reads, namespaced by chapter — `inseason.price`, `exit.consolidation`. */
  readonly kind: string;
  /** The market the page is scoped to, or `null` when the run names its own. */
  readonly market?: string | null;
}

/**
 * The key one planner's choice on one scope is filed under — in this tab's memory of what was
 * chosen for them, and in the leftover storage it also names. Pure.
 */
export function runMemoryKey(subject: string | null, scope: RunScope): string {
  const who = subject === null || subject.trim() === '' ? 'anonymous' : subject.trim();
  return [RUN_MEMORY_PREFIX, encodeURIComponent(who), scope.kind, scope.market ?? ANY_MARKET].join(':');
}

/** Forget a choice a previous version of the page kept in this browser. */
export function forgetRun(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // Nothing to forget where nothing could be stored.
  }
}

/** Which run the page is on, and why. */
export type RunResolution =
  /** The URL names it. */
  | { readonly source: 'url'; readonly runId: string }
  /** The page's list has not answered yet. */
  | { readonly source: 'pending' }
  /** The newest offered. */
  | { readonly source: 'newest'; readonly runId: string }
  /** Nothing to open: no run of this kind exists, or the list could not be read. */
  | { readonly source: 'none' };

/**
 * The precedence: the URL, else the newest offered, else none. `offered` is the page's own list of
 * eligible runs, newest first — `undefined` while it loads, `[]` when it is empty or failed. Pure.
 */
export function resolveRun(
  urlRunId: string | null,
  offered: readonly string[] | undefined,
): RunResolution {
  if (urlRunId !== null) return { source: 'url', runId: urlRunId };
  if (offered === undefined) return { source: 'pending' };
  const newest = offered[0];
  if (newest === undefined) return { source: 'none' };
  return { source: 'newest', runId: newest };
}

/** How the run now in the URL was chosen for the planner, kept for the note. */
export interface AutoChoice {
  readonly runId: string;
  readonly source: 'newest';
}

/**
 * The choices made for the planner in this tab, by storage key. Module state on purpose: the
 * component that chose (a «Scegli un run» gate) unmounts the moment the URL names the run, and the
 * picker that says so is another component. A reload clears it, which is the "said once" of a
 * stale memory: by then the memory is already forgotten.
 */
const autoChoices = new Map<string, AutoChoice>();

/** The choice made for the planner on `key`, while the URL still names its run. */
export function autoChoiceFor(key: string, urlRunId: string | null): AutoChoice | null {
  const choice = autoChoices.get(key);
  return choice !== undefined && choice.runId === urlRunId ? choice : null;
}

/** Test seam: forget every choice made in this tab. */
export function clearAutoChoices(): void {
  autoChoices.clear();
}

export interface UseRunDefaultOptions {
  readonly scope: RunScope;
  /** The run the URL names, or `null`. */
  readonly urlRunId: string | null;
  /** The page's eligible runs, newest first; `undefined` while loading, `[]` when empty or failed. */
  readonly offered: readonly string[] | undefined;
  /** Write a run chosen for the planner into the URL, replacing the history entry. */
  readonly write: (runId: string) => void;
  /** `false` turns the default off — a page still missing another part of its scope. */
  readonly enabled?: boolean;
}

export interface UseRunDefault {
  /** The storage key — what {@link RunDefaultNote} is told. */
  readonly key: string;
  readonly resolution: RunResolution;
  /** The planner chose `runId` by hand: it is no longer one chosen for them. The caller navigates. */
  readonly choose: (runId: string) => void;
  /**
   * The planner chose «Il più recente». The callers then drop the run from the
   * URL, so the page opens on the newest by itself (and says so). Returns the newest offered run
   * when the list is known — `null` on a page whose URL names a run, which reads no list.
   */
  readonly chooseNewest: () => string | null;
}

/** The signed-in subject the memory is keyed by — the login, or `null`. */
function useSubject(): string | null {
  return useAuthSession().actor;
}

/**
 * Resolve the page's run and, when it was chosen for the planner, write it into the URL — once
 * per resolution, after the render that computed it.
 */
export function useRunDefault({ scope, urlRunId, offered, write, enabled = true }: UseRunDefaultOptions): UseRunDefault {
  const key = runMemoryKey(useSubject(), scope);
  const resolution: RunResolution = enabled ? resolveRun(urlRunId, offered) : { source: 'none' };

  const target = resolution.source === 'newest' ? resolution.runId : null;
  useEffect(() => {
    if (target === null) return;
    autoChoices.set(key, { runId: target, source: 'newest' });
    write(target);
    // `write` is the caller's and may be a fresh closure each render; the run decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, target]);

  const firstOffered = offered?.[0] ?? null;
  const choose = useCallback((): void => {
    // The URL carries the planner's choice, so there is nothing to keep: only to say the run is
    // no longer one chosen for them, and to drop what an earlier version kept in this browser.
    autoChoices.delete(key);
    forgetRun(key);
  }, [key]);
  const chooseNewest = useCallback((): string | null => {
    // Forget what was said about the choice; the default then opens the newest afresh.
    forgetRun(key);
    autoChoices.delete(key);
    return firstOffered;
  }, [key, firstOffered]);

  return { key, resolution, choose, chooseNewest };
}

/**
 * What the page says about a run chosen for the planner, beside the picker — and nothing for a
 * run the URL carried or the planner picked: a caption (`newestKey`, «Scelto per te: il più
 * recente»), drawn while the URL still names the run this module chose.
 */
export function RunDefaultNote({ memoryKey, runId, newestKey = 'runDefault.chosenNewest', showNewest = true }: {
  readonly memoryKey: string;
  /** The run the URL names. */
  readonly runId: string | null;
  /** The caption for a run chosen as the newest — a page may name its kind. */
  readonly newestKey?: string;
  /** `false`: no caption (a page whose subject IS the newest). */
  readonly showNewest?: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!showNewest || autoChoiceFor(memoryKey, runId) === null) return null;
  return (
    <span className="text-caption text-ink-secondary flex flex-wrap items-baseline gap-2" data-run-default="newest">
      <span>{t(newestKey)}</span>
    </span>
  );
}
